import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { notificationApi } from '../../services/api';
import {
  MessageSquare,
  LayoutDashboard,
  Settings,
  LogOut,
  Menu,
  X,
  BookOpen,
  GraduationCap,
  Video,
  Bell,
  Upload,
  ClipboardCheck,
  ChevronDown,
  ChevronRight,
  User,
  Sparkles
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

function Layout() {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lmsExpanded, setLmsExpanded] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);
  const isChatPage = location.pathname === '/chat';

  // Close notification dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target) && !e.target.closest('[data-notif-bell]')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Allow child pages (e.g. StudentChat) to open the sidebar via custom event
  useEffect(() => {
    const handler = () => setSidebarOpen(true);
    window.addEventListener('open-sidebar', handler);
    return () => window.removeEventListener('open-sidebar', handler);
  }, []);

  useEffect(() => {
    loadNotificationCount();

    // Poll notification count every 30 seconds
    const interval = setInterval(() => {
      loadNotificationCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const loadNotificationCount = async () => {
    try {
      const response = await notificationApi.getUnreadCount();
      setUnreadNotifications(response.data.count);
    } catch (error) {
      console.error('Failed to load notification count:', error);
    }
  };

  const toggleNotifications = async () => {
    if (!showNotifications) {
      try {
        const response = await notificationApi.list();
        setNotifications(response.data.slice(0, 10));
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    }
    setShowNotifications(!showNotifications);
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await notificationApi.markRead(notif.id);
        setUnreadNotifications(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      } catch (error) { /* ignore */ }
    }
    if (notif.link) {
      window.location.href = notif.link;
    }
    setShowNotifications(false);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationApi.markAllRead();
      setUnreadNotifications(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) { /* ignore */ }
  };

  const navigation = {
    student: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'My Profile', href: '/profile', icon: User },
      { name: 'Ask Doubts', href: '/chat', icon: MessageSquare },
      { name: 'AI Notebooks', href: '/notebooks', icon: Sparkles },
      { name: 'Study Materials', href: '/lms/courses', icon: BookOpen },
      { name: 'Mock Tests', href: '/lms/mock-tests', icon: ClipboardCheck },
    ],
    admin: [
      { name: 'Admin', href: '/admin', icon: Settings },
      { name: 'Ask Doubts', href: '/chat', icon: MessageSquare },
    ]
  };

  const lmsLinks = {
    student: [
      { name: 'Study Materials', href: '/lms/courses', icon: BookOpen },
      { name: 'Live Sessions', href: '/lms/live-sessions', icon: Video },
      { name: 'My Submissions', href: '/lms/submissions', icon: Upload },
    ],
    admin: [
      { name: 'Manage LMS', href: '/lms/manage', icon: Settings },
      { name: 'Submissions', href: '/lms/submissions', icon: ClipboardCheck },
    ]
  };

  const links = navigation[user?.role] || navigation.student;
  const lmsSubLinks = lmsLinks[user?.role] || lmsLinks.student;
  const isLmsActive = location.pathname.startsWith('/lms');

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b">
            <Link to="/" className="text-xl font-bold text-primary-600">
              ExamPrep
            </Link>
            <div className="flex items-center space-x-1">
              {/* Notification Bell (sidebar - visible on lg+) */}
              <button
                onClick={toggleNotifications}
                className="p-2 rounded-md hover:bg-gray-100 relative"
                data-notif-bell
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {unreadNotifications > 0 && (
                  <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </button>

              <button
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {links.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`
                    flex items-center justify-between px-3 py-2 rounded-lg transition-colors
                    ${isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="flex items-center">
                    <Icon className="w-5 h-5 mr-3" />
                    {link.name}
                  </div>
                  {link.badge > 0 && (
                    <span className="bg-primary-600 text-white text-xs rounded-full px-2 py-0.5">
                      {link.badge}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* LMS Section (collapsible) - hidden for students */}
            {user?.role !== 'student' && <div className="mt-2 pt-2 border-t">
              <button
                onClick={() => setLmsExpanded(!lmsExpanded)}
                className={`
                  flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors
                  ${isLmsActive ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'}
                `}
              >
                <div className="flex items-center">
                  <GraduationCap className="w-5 h-5 mr-3" />
                  LMS
                </div>
                {lmsExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {lmsExpanded && (
                <div className="mt-1 ml-2 space-y-0.5">
                  {lmsSubLinks.map((link) => {
                    const Icon = link.icon;
                    const isActive = location.pathname === link.href;
                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        className={`
                          flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors
                          ${isActive
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                          }
                        `}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="w-4 h-4 mr-3" />
                        {link.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>}

          </nav>

          {/* User info */}
          <div className="p-4 border-t">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-700 font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center w-full px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-50"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header - hidden on chat page which has its own header */}
        <header className={`lg:hidden ${isChatPage ? 'hidden' : 'flex'} items-center justify-between h-16 px-4 border-b bg-white`}>
          <div className="flex items-center">
            <button
              className="p-2 rounded-md hover:bg-gray-100"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="ml-3 text-lg font-semibold">ExamPrep</span>
          </div>
          <button
            onClick={toggleNotifications}
            className="p-2 rounded-md hover:bg-gray-100 relative"
            data-notif-bell
          >
            <Bell className="w-5 h-5 text-gray-600" />
            {unreadNotifications > 0 && (
              <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>
        </header>

        {/* Page content - chat page manages its own scrolling */}
        <main className={`flex-1 ${isChatPage ? 'overflow-hidden' : 'overflow-auto'}`}>
          <Outlet />
        </main>
      </div>

      {/* Notification Dropdown - rendered at page level */}
      {showNotifications && (
        <div ref={notifRef} className="fixed top-16 right-4 lg:left-64 lg:right-auto w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-[100] max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-900 text-sm">Notifications</h3>
            {unreadNotifications > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                Mark all read
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              No notifications
            </div>
          ) : (
            notifications.map(notif => (
              <button
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                  !notif.is_read ? 'bg-primary-50/50' : ''
                }`}
              >
                <p className={`text-sm ${!notif.is_read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                  {notif.title}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                <p className="text-[10px] text-gray-400 mt-1">
                  {new Date(notif.created_at).toLocaleDateString()} {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default Layout;
